import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import type { GetAccountDetailResponse } from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import { useAdjustBalance } from '../api/useAdmin.ts';
import {
  formatUsdCents,
  formatUsdMicrosOrCentsBalance,
  parseAmountInputToMinorUnits,
  usdMicrosFromCentsFallback,
} from './formatters.ts';
import { Modal } from './Modal.tsx';
import { useToast, coerceErrorMessage } from './Toast.tsx';
import { useT } from '../i18n/index.ts';
import { Button, FormField, Input, Panel, Textarea } from '../ui/index.ts';

/**
 * Admin-only balance adjustment card + type-to-confirm modal.
 *
 * Extracted from `pages/AccountDetail.tsx` for the Customer 360 merge
 * (#861 PR A) so the 360 page can host the same interaction. The inline
 * AccountDetail copy is removed in PR B when that page becomes a
 * resolving redirect.
 *
 * Money-safety on the adjustment form:
 *   - Amount accepts a leading `-` for debits (intentional; negative
 *     adjustments are allowed to drive the balance below zero).
 *   - Submit opens a confirmation modal that shows target email + current
 *     balance + amount (color coded) + resulting balance.
 *   - The modal requires the operator to TYPE the amount (with the sign)
 *     exactly to enable Submit — same Wave 5C pattern as gift-card
 *     redeem-on-behalf.
 *   - `external_request_id` is minted at modal mount via
 *     `crypto.randomUUID()` so a button-click replay dedupes idempotently
 *     downstream.
 *
 * Spoof-resistance: the client NEVER sends `actor_user_id`. ops-api
 * extracts it from the session cookie and overrides any client value.
 */

function formatSigned(amount: number): string {
  return formatUsdCents(amount, true);
}

interface BalanceAdjustmentCardProps {
  readonly account: GetAccountDetailResponse;
}

interface DraftAdjustment {
  readonly amountUsdCents: number;
  readonly amountRaw: string;
  readonly reason: string;
}

/**
 * Finance preset modes on top of the generic signed adjustment (Epic
 * #109 / P1.8). `custom` keeps the pre-existing behaviour (signed amount
 * + free-form reason). `refund` and `sla_credit` constrain the amount to
 * positive values and auto-prefix the reason so
 * `balance_transactions.reason LIKE 'refund:%'` / `'sla_credit:%'` lands
 * as a clean category for the monthly close report — the backend ledger
 * is untouched; finance just gets a structured prefix on the text side.
 */
type AdjustmentMode = 'custom' | 'refund' | 'sla_credit';

function reasonPrefix(mode: AdjustmentMode): string {
  if (mode === 'refund') return 'refund: ';
  if (mode === 'sla_credit') return 'sla_credit: ';
  return '';
}

export function BalanceAdjustmentCard({ account }: BalanceAdjustmentCardProps): JSX.Element {
  const t = useT();
  const [mode, setMode] = useState<AdjustmentMode>('custom');
  const [amountRaw, setAmountRaw] = useState('');
  const [reason, setReason] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [draft, setDraft] = useState<DraftAdjustment | null>(null);

  function switchMode(next: AdjustmentMode): void {
    setMode(next);
    setFormError(null);
    // Wipe the reason / amount when switching modes — carrying over a
    // negative amount from `custom` into `refund` would silently fail
    // the positive-amount gate, and keeping a prior `refund: X` reason
    // when flipping to `sla_credit` would land the wrong prefix.
    setReason('');
    setAmountRaw('');
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setFormError(null);
    const trimmedAmount = amountRaw.trim();
    if (trimmedAmount.length === 0) {
      setFormError(t('account_detail.adjust.error.amount_required'));
      return;
    }
    const parsed = parseAmountInputToMinorUnits(trimmedAmount);
    if (!parsed.ok || parsed.minorUnits === 0) {
      setFormError(t('account_detail.adjust.error.amount_non_zero'));
      return;
    }
    if ((mode === 'refund' || mode === 'sla_credit') && parsed.minorUnits <= 0) {
      setFormError(t('account_detail.adjust.error.positive_required'));
      return;
    }
    const trimmedReason = reason.trim();
    if (trimmedReason.length === 0) {
      setFormError(t('account_detail.adjust.error.reason_required'));
      return;
    }
    const prefixed = `${reasonPrefix(mode)}${trimmedReason}`;
    setDraft({ amountUsdCents: parsed.minorUnits, amountRaw: parsed.normalized, reason: prefixed });
  }

  function handleConfirmClose(success: boolean): void {
    setDraft(null);
    if (success) {
      setAmountRaw('');
      setReason('');
    }
  }

  return (
    <Panel
      caption={t('account_detail.adjust.title')}
      actions={
        <span className="text-micro text-paper-low">
          {t('account_detail.adjust.subtitle')}
        </span>
      }
      padded
      testId="account-adjust-card"
    >
      <div className="flex flex-col gap-4">
        <div
          role="radiogroup"
          aria-label={t('account_detail.adjust.mode.aria_label')}
          data-testid="account-adjust-mode"
          className="flex flex-wrap items-center gap-1 self-start rounded-lg border border-line-soft bg-canvas p-1"
        >
          {(['custom', 'refund', 'sla_credit'] as const).map((m) => {
            const active = mode === m;
            return (
              <button
                key={m}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => switchMode(m)}
                data-testid={`account-adjust-mode-${m}`}
                data-active={active ? 'true' : 'false'}
                className={`inline-flex items-center justify-center rounded px-3 py-1.5 text-dense font-medium transition-colors duration-120 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40 ${
                  active
                    ? 'bg-signal-dim text-signal'
                    : 'text-paper-mid hover:bg-raised hover:text-paper'
                }`}
              >
                {t(
                  m === 'custom'
                    ? 'account_detail.adjust.mode.custom'
                    : m === 'refund'
                    ? 'account_detail.adjust.mode.refund'
                    : 'account_detail.adjust.mode.sla_credit',
                )}
              </button>
            );
          })}
        </div>
        <p className="text-label text-paper-low" data-testid="account-adjust-mode-hint">
          {t(
            mode === 'custom'
              ? 'account_detail.adjust.mode.custom.hint'
              : mode === 'refund'
              ? 'account_detail.adjust.mode.refund.hint'
              : 'account_detail.adjust.mode.sla_credit.hint',
          )}
        </p>
        <form className="flex flex-col gap-4" onSubmit={handleSubmit} noValidate>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField
              label={t('account_detail.adjust.amount.label')}
              fieldId="account-adjust-amount"
            >
              <Input
                id="account-adjust-amount"
                type="text"
                inputMode="decimal"
                mono
                value={amountRaw}
                onChange={(e) => setAmountRaw(e.currentTarget.value)}
                placeholder={t('account_detail.adjust.amount.placeholder')}
                data-testid="account-adjust-amount"
                autoComplete="off"
              />
            </FormField>
            <FormField
              label={t('account_detail.adjust.reason.label')}
              fieldId="account-adjust-reason"
              className="sm:row-span-2"
            >
              <Textarea
                id="account-adjust-reason"
                value={reason}
                onChange={(e) => setReason(e.currentTarget.value)}
                rows={3}
                placeholder={t(
                  mode === 'refund'
                    ? 'account_detail.adjust.reason.placeholder_refund'
                    : mode === 'sla_credit'
                    ? 'account_detail.adjust.reason.placeholder_sla_credit'
                    : 'account_detail.adjust.reason.placeholder',
                )}
                data-testid="account-adjust-reason"
                className="min-h-[72px] resize-y"
              />
            </FormField>
          </div>
          {formError !== null ? (
            <p
              className="text-label text-bad"
              role="alert"
              data-testid="account-adjust-error"
            >
              {formError}
            </p>
          ) : null}
          <div className="flex items-center justify-end">
            <Button type="submit" variant="primary" data-testid="account-adjust-submit">
              {t('account_detail.adjust.submit')}
            </Button>
          </div>
        </form>
      </div>

      {draft !== null ? (
        <AdjustConfirmModal
          account={account}
          draft={draft}
          onClose={(success) => handleConfirmClose(success)}
        />
      ) : null}
    </Panel>
  );
}

interface AdjustConfirmModalProps {
  readonly account: GetAccountDetailResponse;
  readonly draft: DraftAdjustment;
  readonly onClose: (success: boolean) => void;
}

function AdjustConfirmModal({
  account,
  draft,
  onClose,
}: AdjustConfirmModalProps): JSX.Element {
  const t = useT();
  const adjust = useAdjustBalance();
  const toast = useToast();
  const [typed, setTyped] = useState('');
  const [serverError, setServerError] = useState<string | null>(null);
  // Mint a fresh idempotency key for this modal instance. useMemo is not
  // stable across remounts (we WANT a fresh key if the operator reopens the
  // modal) but stable for the life of this modal — a double-click on the
  // submit button will therefore replay the same external_request_id.
  const externalRequestId = useMemo(() => crypto.randomUUID(), []);

  // `typed` must match the raw amount the operator drafted (with sign).
  const typedMatches = typed.trim() === draft.amountRaw;
  const amountClass = draft.amountUsdCents > 0 ? 'text-ok' : 'text-bad';
  const currentBalanceUsdMicros = usdMicrosFromCentsFallback(
    account.balance_usd_micros,
    account.balance_usd_cents,
  );
  const resultingBalance = currentBalanceUsdMicros + draft.amountUsdCents * 10_000;
  const resultingClass = resultingBalance < 0 ? 'text-bad' : 'text-paper';

  useEffect(() => {
    setServerError(null);
  }, [typed]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setServerError(null);
    if (!typedMatches) {
      setServerError(t('account_detail.confirm.type_to_confirm', { raw: draft.amountRaw }));
      return;
    }
    try {
      await adjust.mutateAsync({
        accountId: account.account_id,
        body: {
          amount_usd_cents: draft.amountUsdCents,
          reason: draft.reason,
          external_request_id: externalRequestId,
        },
      });
      onClose(true);
    } catch (err) {
      const message =
        err instanceof OpsApiError
          ? `${err.code}: ${err.message}`
          : coerceErrorMessage(err);
      setServerError(message);
      toast.error(message);
    }
  }

  return (
    <Modal
      isOpen
      onClose={() => {
        if (!adjust.isPending) onClose(false);
      }}
      titleId="adjust-confirm-title"
      testId="account-adjust-modal"
    >
      <form className="flex flex-col gap-5 p-6" onSubmit={handleSubmit} noValidate>
        <h2 id="adjust-confirm-title" className="text-title text-paper">
          {t('account_detail.confirm.title')}
        </h2>
        <div className="flex flex-col gap-3 rounded-lg bg-well p-4">
          <Row
            label={t('account_detail.confirm.target_account')}
            value={account.email}
            testId="account-adjust-modal-email"
          />
          <Row
            label={t('account_detail.confirm.current_balance')}
            value={formatUsdMicrosOrCentsBalance(account.balance_usd_micros, account.balance_usd_cents)}
            mono
            testId="account-adjust-modal-current"
          />
          <Row
            label={t('account_detail.confirm.adjustment')}
            value={formatSigned(draft.amountUsdCents)}
            mono
            valueClass={amountClass}
            testId="account-adjust-modal-delta"
          />
          <Row
            label={t('account_detail.confirm.resulting_balance')}
            value={formatUsdMicrosOrCentsBalance(resultingBalance, null)}
            mono
            valueClass={resultingClass}
            testId="account-adjust-modal-result"
          />
        </div>
        <FormField
          label={t('account_detail.confirm.type_to_confirm', { raw: draft.amountRaw })}
          fieldId="account-adjust-modal-typed"
        >
          <Input
            id="account-adjust-modal-typed"
            type="text"
            inputMode="decimal"
            mono
            value={typed}
            onChange={(e) => setTyped(e.currentTarget.value)}
            placeholder={draft.amountRaw}
            data-testid="account-adjust-modal-typed"
            disabled={adjust.isPending}
            autoComplete="off"
          />
        </FormField>
        <p className="text-micro text-paper-low">
          {t('account_detail.confirm.idempotency_key')}{' '}
          <span
            className="font-mono text-paper-mid"
            data-testid="account-adjust-modal-request-id"
          >
            {externalRequestId}
          </span>
        </p>
        {serverError !== null ? (
          <p
            className="text-label text-bad"
            role="alert"
            data-testid="account-adjust-modal-error"
          >
            {serverError}
          </p>
        ) : null}
        <div className="flex items-center justify-end gap-3">
          <Button
            onClick={() => {
              if (!adjust.isPending) onClose(false);
            }}
            disabled={adjust.isPending}
            data-testid="account-adjust-modal-cancel"
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={!typedMatches}
            loading={adjust.isPending}
            data-testid="account-adjust-modal-submit"
          >
            {adjust.isPending
              ? t('account_detail.confirm.submitting')
              : t('account_detail.confirm.submit', { signed: formatSigned(draft.amountUsdCents) })}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

interface RowProps {
  readonly label: string;
  readonly value: string;
  readonly mono?: boolean;
  readonly valueClass?: string;
  readonly testId?: string;
}

function Row({ label, value, mono, valueClass, testId }: RowProps): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-label text-paper-low">
        {label}
      </span>
      <span
        className={`text-body font-semibold ${mono === true ? 'font-mono' : ''} ${
          valueClass ?? 'text-paper'
        }`}
        data-testid={testId}
      >
        {value}
      </span>
    </div>
  );
}
